/**
 * NotificationCenter Component
 * Displays notifications with bell icon and dropdown panel
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './NotificationCenter.css';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { getAccessToken, user } = useAuth();
  const dropdownRef = useRef(null);
  
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadNotifications();
    
    // Poll for new notifications every 60 seconds
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      
      if (!user || !user.email) {
        console.error('User email not available');
        return;
      }
      
      const accessToken = await getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'https://demo-api.example.com/prod';
      
      const response = await fetch(`${apiUrl}/leading/api/notifications?user_email=${encodeURIComponent(user.email)}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load notifications');
      }

      const data = await response.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
      
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      if (!user || !user.email) {
        console.error('User email not available');
        return;
      }
      
      const accessToken = await getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'https://demo-api.example.com/prod';
      
      await fetch(`${apiUrl}/leading/api/notifications/${notificationId}/read?user_email=${encodeURIComponent(user.email)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_email: user.email })
      });

      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, is_read: true } 
            : n
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const deleteNotification = async (notificationId, event) => {
    event.stopPropagation();
    
    try {
      if (!user || !user.email) {
        console.error('User email not available');
        return;
      }
      
      const accessToken = await getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'https://demo-api.example.com/prod';
      
      await fetch(`${apiUrl}/leading/api/notifications/${notificationId}?user_email=${encodeURIComponent(user.email)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      // Update local state
      setNotifications(prev => 
        prev.filter(n => n.id !== notificationId)
      );
      
      // Update unread count if notification was unread
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Navigate to action URL if provided
    if (notification.action_url) {
      setIsOpen(false);
      navigate(notification.action_url);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'SURVEY_ASSIGNED': '📋',
      'REMINDER': '⏰',
      'COMPLETED': '✅',
      'SURVEY_CLOSED': '🔒',
      'COHORT_STARTED': '🚀'
    };
    return icons[type] || '📬';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="notification-center" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button 
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <span className="unread-count">{unreadCount} unread</span>
            )}
          </div>

          <div className="notification-list">
            {loading && notifications.length === 0 && (
              <div className="notification-loading">
                <div className="spinner-small"></div>
                <p>Loading...</p>
              </div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="notification-empty">
                <div className="empty-icon">🔔</div>
                <p>No notifications</p>
              </div>
            )}

            {notifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-icon">
                  {getNotificationIcon(notification.type)}
                </div>
                
                <div className="notification-content">
                  <p className="notification-message">{notification.message}</p>
                  <span className="notification-time">
                    {formatTimestamp(notification.created_at)}
                  </span>
                </div>

                <button
                  className="notification-delete"
                  onClick={(e) => deleteNotification(notification.id, e)}
                  aria-label="Delete notification"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <button 
                className="view-all-button"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/leading/notifications');
                }}
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
